import Instance from "@/services/general.instance";
import JwtService from "@/common/JwtService";
import LocalStorageService from "@/common/LocalStorageService";
import {
  LOGIN,
  LOGOUT,
  REGISTER,
  CHECK_AUTH,
  RESET_PASSWORD,
  FORCE_LOGOUT,
  REFRESH_TOKEN
} from "../constants/actions.type";
import {
  SET_AUTH,
  PURGE_AUTH,
  SET_ERROR,
  SET_LOADING,
  SET_AUTH_REFRESH
} from "../constants/mutations.type";
import { AUTH_LOGIN, AUTH_REGISTER, AUTH_INSTANCE } from "@/services/constants";
import AuthService from "@/services/resources/auth.service";

const state = {
  errors: null,
  user: {},
  isAuthenticated: !!JwtService.getToken(),
  token: JwtService.getToken(),
  loading: false,
  loadingLogout: false
};

const getters = {
  currentUser(state) {
    return state.user;
  },
  authLoading(state) {
    return state.loading;
  },
  authLogout(state) {
    return state.loadingLogout;
  },
  isAuthenticated(state) {
    return state.isAuthenticated;
  },
  parsedJWT(state) {
    if (state.token) {
      var jwtData = JwtService.parseJwt(state.token);
      jwtData.acronymName = jwtData.full_name
        ? jwtData.full_name
            .match(/\b(\w)/g)
            .join("")
            .substring(0, 2)
        : "Anonymous";
      return jwtData;
    }
    return null;
  }
};

const actions = {
  [LOGIN](context, credentials) {
    return new Promise((resolve, reject) => {
      try {
        context.commit(SET_LOADING, true);
        AuthService.login(credentials)
          .then(({ data }) => {
            context.commit(SET_AUTH, data);
            resolve(data);
          })
          .catch(({ response: { status, data } = {} }) => {
            if (
              status === 401 &&
              data.error_description === "Invalid Username"
            ) {
              context.commit(SET_ERROR, "Invalid Username");
              reject("Invalid Username");
            } else if (
              status === 401 &&
              data?.error_description
                ?.toLowerCase()
                .includes("your password is expired")
            ) {
              context.commit(SET_ERROR, "Password is Expired");
              context.commit("snackbar/setSnack", {
                show: true,
                message: data.error_description,
                color: "error"
              });
              reject("Password is Expired");
            } else if (status === 400 && data.result === "Bad credentials") {
              context.commit(SET_ERROR, "Invalid Password");
              reject("Invalid Password");
            } else if (status === 500) {
              context.commit("snackbar/setSnack", {
                show: true,
                message:
                  "Something went wrong. Please try again or contact admin",
                color: "error"
              });
              reject("Something went wrong. Please try again or contact admin");
            } else {
              const message = `Please try again or contact admin ${
                status ? `, Error Code ${status}` : ""
              }`;
              context.commit("snackbar/setSnack", {
                show: true,
                message,
                color: "error"
              });
              reject(message);
            }
          })
          .finally(() => context.commit(SET_LOADING, false));
      } catch (error) {
        this.$store.commit("snackbar/setSnack", {
          show: true,
          message: "Something went wrong. Please try again or contact admin",
          color: "error"
        });
        console.error(error);
        reject(error);
      }
    });
  },
  [RESET_PASSWORD](context, credentials) {
    return new Promise(resolve => {
      const AuthInstance = new Instance(AUTH_INSTANCE);

      AuthInstance.post(AUTH_LOGIN, credentials)
        .then(({ data }) => {
          context.commit(SET_AUTH, data.user);
          resolve(data);
        })
        .catch(({ response }) => {
          if (response) {
            context.commit(SET_ERROR, response.data.message);
          }
        });
    });
  },
  [LOGOUT](context) {
    return new Promise((resolve, reject) => {
      context.commit(SET_LOADING, true);
      AuthService.logout(JwtService.getToken())
        .then(({ data }) => {
          context.commit(PURGE_AUTH);
          resolve(data);
        })
        .catch(({ response }) => {
          if (response) {
            context.commit(SET_ERROR, response.data.message);
          }
          reject(response);
        })
        .finally(() => context.commit(SET_LOADING, false));
    });
  },
  [FORCE_LOGOUT](context) {
    context.commit(PURGE_AUTH);
    context.commit(SET_LOADING, false);
  },
  [REGISTER](context, credentials) {
    return new Promise((resolve, reject) => {
      const AuthInstance = new Instance(AUTH_INSTANCE);

      AuthInstance.post(AUTH_REGISTER, { user: credentials })
        .then(({ data }) => {
          context.commit(SET_AUTH, data.user);
          resolve(data);
        })
        .catch(({ response }) => {
          if (response) {
            context.commit(SET_ERROR, response.data.message);
          }
          reject(response);
        });
    });
  },
  [REFRESH_TOKEN](context, credentials) {
    return new Promise((resolve, reject) => {
      AuthService.refreshToken(credentials)
        .then(({ data }) => {
          context.commit(SET_AUTH_REFRESH, data);
          resolve(data);
        })
        .catch(error => {
          reject(error);
          throw new Error(error);
        });
    });
  },
  [CHECK_AUTH](context) {
    if (JwtService.getToken()) {
      return;
    } else {
      context.commit(PURGE_AUTH);
    }
  }
};

const mutations = {
  [SET_ERROR](state, error) {
    state.errors = error;
  },
  [SET_LOADING](state, data) {
    state.loading = data;
  },
  [SET_AUTH](state, data) {
    state.isAuthenticated = true;
    state.errors = null;
    state.token = data.access_token;
    LocalStorageService.saveUsername(data.username);
    JwtService.saveToken(data.access_token);
    JwtService.saveRefreshToken(data.refresh_token);
  },
  [SET_AUTH_REFRESH](state, data) {
    state.token = data.access_token;
    JwtService.saveToken(data.access_token);
  },
  [PURGE_AUTH](state) {
    state.isAuthenticated = false;
    state.token = null;
    state.user = {};
    state.errors = null;
    JwtService.destroyToken();
    JwtService.destroyRefreshToken();
    LocalStorageService.destroyUsername();
    LocalStorageService.destroyMenu();
    LocalStorageService.destroyProfile();
  }
};

export default {
  state,
  actions,
  mutations,
  getters
};
